import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Building2, LibraryBig, Heart, Bookmark, Clock3, RefreshCw, Gift, UserRound,
  HelpCircle, Sparkles, Menu, X, Search, ChevronDown, Lock, ArrowRight, LogOut, Bell, Coins, Plus, FileText, Lightbulb, MessageSquarePlus,
} from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { useAuth } from "@/context/AuthContext";
import { useCredits } from "@/context/CreditsContext";
import { libApi } from "@/lib/api";
import paymentsCopy from "@/data/payments-copy.json";
import { APP, PAYMENTS } from "@/constants/testIds";

const MENU = [
  { to: "/biblioteka", label: "Moja biblioteka", icon: LibraryBig, testId: APP.navLibrary, end: true },
  { to: "/asystent", label: "Asystent", icon: Sparkles, testId: "nav-assistant" },
  { to: "/ulubione", label: "Ulubione", icon: Heart, testId: APP.navFavorites },
  { to: "/zapisane-prompty", label: "Zapisane prompty", icon: Bookmark, testId: APP.navSaved },
  { to: "/ostatnio-uzywane", label: "Ostatnio używane", icon: Clock3, testId: APP.navRecent },
  { to: "/aktualizacje", label: "Aktualizacje", icon: RefreshCw, testId: APP.navUpdates },
  { to: "/bonusy", label: "Bonusy", icon: Gift, testId: APP.navBonuses },
  { to: "/konto", label: "Konto", icon: UserRound, testId: APP.navAccount },
];

function SidebarContent({ activePackages, currentSlug, onNavigate }) {
  const secondaryCls = ({ isActive }) =>
    `flex items-center gap-3 rounded-field px-3 py-2.5 text-[15px] font-medium transition-colors duration-200 ${
      isActive ? "bg-brand-elevated text-brand-text" : "text-brand-sec hover:bg-brand-elevated hover:text-brand-text"
    }`;

  const toolCls = ({ isActive }) =>
    `group flex w-full items-center gap-2.5 rounded-field px-3 py-2.5 text-sm font-medium outline-none transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg active:bg-brand-accent/15 ${
      isActive ? "bg-brand-elevated text-brand-text" : "text-brand-sec hover:bg-brand-elevated hover:text-brand-text"
    }`;

  return (
    <div className="flex h-full flex-col">
      <p className="px-3 pb-2 pt-1 text-xs font-semibold uppercase tracking-wider text-brand-muted">
        Kategorie
      </p>

      {activePackages.map((pkg) => {
        const active = currentSlug === pkg.slug;
        return (
          <Link
            key={pkg.slug}
            to={`/biblioteka/${pkg.slug}`}
            data-testid={APP.navLibraryActive}
            onClick={onNavigate}
            className={`flex items-center gap-3 rounded-field px-3 py-2.5 text-[15px] font-medium transition-colors duration-200 ${
              active ? "bg-brand-accent text-brand-on" : "text-brand-sec hover:bg-brand-elevated hover:text-brand-text"
            }`}
          >
            <Building2 size={18} strokeWidth={1.75} />
            <span className="flex-1 truncate">{pkg.name}</span>
            {pkg.new_count > 0 && (
              <span
                data-testid={`package-new-badge-${pkg.slug}`}
                title={`${pkg.new_count} nowych lub zaktualizowanych promptów`}
                className={`flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${
                  active ? "bg-brand-on/20 text-brand-on" : "bg-brand-accent text-brand-on"
                }`}
              >
                {pkg.new_count}
              </span>
            )}
          </Link>
        );
      })}

      <div className="my-3 border-t border-brand-line" />
      <p className="px-3 pb-2 pt-1 text-xs font-semibold uppercase tracking-wider text-brand-muted">
        Polecane narzędzia
      </p>

      <NavLink to="/cv" data-testid="nav-cv" onClick={onNavigate} className={toolCls}>
        <FileText size={18} strokeWidth={1.75} className="shrink-0" />
        <span className="min-w-0 flex-1 truncate">CV i kariera</span>
        <span
          data-testid="nav-cv-new-badge"
          className="shrink-0 rounded-full bg-brand-accent px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-brand-on"
        >
          Nowość
        </span>
      </NavLink>

      <NavLink to="/biznes" data-testid="nav-biznes" onClick={onNavigate} className={toolCls}>
        <Lightbulb size={18} strokeWidth={1.75} className="shrink-0" />
        <span className="min-w-0 flex-1 truncate">Biznes i pomysły</span>
        <span
          data-testid="nav-biznes-new-badge"
          className="shrink-0 rounded-full bg-brand-accent px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-brand-on"
        >
          Nowość
        </span>
      </NavLink>

      <div className="my-3 border-t border-brand-line" />

      {MENU.map(({ to, label, icon: Icon, testId, end }) => (
        <NavLink key={to} to={to} end={end} data-testid={testId} onClick={onNavigate} className={secondaryCls}>
          <Icon size={18} strokeWidth={1.75} /> {label}
        </NavLink>
      ))}

      <div className="my-4 border-t border-brand-line" />

      <NavLink
        to="/pomoc"
        data-testid={APP.navHelp}
        onClick={onNavigate}
        className={({ isActive }) =>
          `mt-2 flex items-center gap-3 rounded-field px-3 py-2.5 text-[15px] font-medium transition-colors duration-200 ${
            isActive ? "bg-brand-elevated text-brand-text" : "text-brand-sec hover:bg-brand-elevated hover:text-brand-text"
          }`
        }
      >
        <HelpCircle size={18} strokeWidth={1.75} /> Pomoc
      </NavLink>

      <button
        type="button"
        data-testid="menu-feedback"
        onClick={() => { onNavigate?.(); window.dispatchEvent(new Event("open-feedback")); }}
        className="mt-1 flex w-full items-center gap-3 rounded-field px-3 py-2.5 text-left text-[15px] font-medium text-brand-sec transition-colors duration-200 hover:bg-brand-elevated hover:text-brand-text"
      >
        <MessageSquarePlus size={18} strokeWidth={1.75} /> Zgłoś problem lub uwagę
      </button>

      <div className="mt-auto px-3 pb-2 pt-6">
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-brand-muted">
          <Link to="/regulamin" onClick={onNavigate} className="hover:text-brand-sec">Regulamin</Link>
          <Link to="/polityka-prywatnosci" onClick={onNavigate} className="hover:text-brand-sec">Prywatność</Link>
          <Link to="/cookies" onClick={onNavigate} className="hover:text-brand-sec">Cookies</Link>
        </div>
      </div>
    </div>
  );
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { balance, openBuy } = useCredits();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [activePackages, setActivePackages] = useState([]);

  const currentSlug = location.pathname.startsWith("/biblioteka/")
    ? location.pathname.split("/")[2]
    : null;

  useEffect(() => {
    let active = true;
    libApi
      .library()
      .then((d) => active && setActivePackages(d.packages || []))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/logowanie", { replace: true });
  };

  return (
    <div data-testid={APP.shell} className="min-h-screen bg-brand-bg lg:flex">
      {/* Desktop sidebar */}
      <aside
        data-testid={APP.sidebar}
        className="hidden w-[260px] shrink-0 flex-col border-r border-brand-line bg-brand-surface px-4 py-6 lg:flex"
      >
        <Link to="/" className="mb-6 px-2" aria-label="Promptivo — strona główna">
          <Logo height={26} />
        </Link>
        <div className="flex-1 overflow-y-auto">
          <SidebarContent activePackages={activePackages} currentSlug={currentSlug} />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b border-brand-line bg-brand-bg/95 px-4 backdrop-blur md:px-8">
          <button
            type="button"
            data-testid={APP.sidebarToggle}
            aria-label="Otwórz menu"
            onClick={() => setOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-field border border-brand-line text-brand-text lg:hidden"
          >
            <Menu size={20} strokeWidth={1.75} />
          </button>

          <div className="relative hidden max-w-xl flex-1 sm:block">
            <Search size={17} strokeWidth={1.75} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              type="search"
              placeholder="Wyszukaj prompt…"
              onKeyDown={(e) => {
                if (e.key === "Enter" && activePackages[0]) navigate(`/biblioteka/${activePackages[0].slug}`);
              }}
              className="h-10 w-full rounded-field border border-brand-line bg-brand-surface pl-11 pr-4 text-sm text-brand-text placeholder:text-brand-muted outline-none transition-colors focus:border-brand-accent/60"
            />
          </div>

          <div className="relative ml-auto flex items-center gap-1.5">
            <div
              data-testid={PAYMENTS.walletChip}
              className="flex items-center gap-1.5 rounded-full border border-brand-line bg-brand-surface px-3 py-1.5 text-sm font-semibold text-brand-text"
              title="Twoje kredyty"
            >
              <Coins size={15} strokeWidth={2} className="text-brand-accent" />
              <span data-testid="wallet-balance-value">{balance == null ? "—" : balance}</span>
            </div>
            <button
              type="button"
              onClick={openBuy}
              data-testid={PAYMENTS.topupButton}
              className="mr-1 inline-flex items-center gap-1.5 rounded-full bg-brand-accent px-3.5 py-1.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
            >
              <Plus size={15} strokeWidth={2.5} /> {paymentsCopy.wallet.topupCta}
            </button>
            <button
              type="button"
              aria-label="Powiadomienia"
              data-testid="topbar-notifications"
              className="relative flex h-10 w-10 items-center justify-center rounded-field text-brand-sec transition-colors hover:bg-brand-surface hover:text-brand-text"
            >
              <Bell size={19} strokeWidth={1.75} />
              <span className="absolute right-2.5 top-2.5 h-1.5 w-1.5 rounded-full bg-brand-accent" />
            </button>
            <button
              type="button"
              onClick={() => setUserMenu((v) => !v)}
              className="flex items-center gap-2 rounded-field px-2 py-1.5 text-sm font-medium text-brand-text transition-colors hover:bg-brand-surface"
            >
              <span className="hidden sm:inline">{user?.first_name || user?.email}</span>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-elevated text-brand-accent">
                {(user?.first_name || user?.email || "?").charAt(0).toUpperCase()}
              </span>
              <ChevronDown size={15} strokeWidth={2} className="text-brand-muted" />
            </button>
            <AnimatePresence>
              {userMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-48 overflow-hidden rounded-card border border-brand-line bg-brand-surface py-1 shadow-xl"
                >
                  <Link to="/konto" onClick={() => setUserMenu(false)} className="block px-4 py-2.5 text-sm text-brand-sec hover:bg-brand-elevated hover:text-brand-text">
                    Konto
                  </Link>
                  {user?.role === "admin" && (
                    <Link
                      to="/admin"
                      data-testid={APP.adminEntryLink || "admin-entry-link"}
                      onClick={() => setUserMenu(false)}
                      className="block px-4 py-2.5 text-sm font-semibold text-brand-accent hover:bg-brand-elevated"
                    >
                      Panel admina
                    </Link>
                  )}
                  <button
                    type="button"
                    data-testid={APP.logoutButton}
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-brand-sec hover:bg-brand-elevated hover:text-brand-text"
                  >
                    <LogOut size={15} strokeWidth={1.75} /> Wyloguj
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </header>

        <main className="min-w-0 flex-1 px-4 py-8 md:px-8 lg:py-10">{children}</main>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-50 lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
            <motion.aside
              className="absolute left-0 top-0 flex h-full w-[280px] flex-col overflow-y-auto bg-brand-surface p-4"
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "tween", duration: 0.25 }}
            >
              <div className="mb-5 flex items-center justify-between px-2">
                <Logo height={26} />
                <button type="button" aria-label="Zamknij menu" onClick={() => setOpen(false)} className="inline-flex h-10 w-10 items-center justify-center rounded-field border border-brand-line text-brand-text">
                  <X size={20} strokeWidth={1.75} />
                </button>
              </div>
              <SidebarContent activePackages={activePackages} currentSlug={currentSlug} onNavigate={() => setOpen(false)} />
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
